import { PROFILE_VIEW_SECTION } from '@workspace/seedforge-shared'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { cn } from '@workspace/ui/lib/utils'
import { Section } from '../components/section.js'
import type { DataRoomDocument, DataRoomProfile } from '../types.js'

export interface DescriptionSectionProps {
  profile: DataRoomProfile
  pitchDeck?: DataRoomDocument | null
  onPitchDeckClick?: () => void
}

/** Tags shown on the public profile. The editor may hold more. */
const MAX_VISIBLE_TAGS = 3

export function DescriptionSection({
  profile,
  pitchDeck,
  onPitchDeckClick,
}: DescriptionSectionProps) {
  // The sector already leads the header meta row; repeating it as a chip 200px
  // later differs only by a slash ("AI & Machine Learning" vs "AI / Machine
  // Learning") and reads as a data bug.
  const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')
  const sector = normalize(profile.sectorLabel || profile.sector || '')
  // Capped: past three these stop being a summary and become a wall of
  // low-information nouns, and every extra chip pushes the description — the
  // only part carrying a claim — further down the first screen.
  const visibleTags = profile.tags
    .filter((tag) => !sector || normalize(tag) !== sector)
    .slice(0, MAX_VISIBLE_TAGS)

  if (!profile.description && visibleTags.length === 0 && !pitchDeck) return null

  return (
    <Section dataSection={PROFILE_VIEW_SECTION.DESCRIPTION}>
      <div className={cn('flex gap-6', pitchDeck ? 'flex-col lg:flex-row' : '')}>
        {/* Description — takes 2/3 when pitch deck present */}
        <div className={cn('min-w-0', pitchDeck ? 'flex-1' : 'w-full')}>
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Description
          </h2>
          {profile.description && (
            <p data-feedback-enabled className="text-sm leading-relaxed text-[#ccc]">
              {profile.description}
            </p>
          )}
          {visibleTags.length > 0 && (
            <div className={cn('flex flex-wrap gap-1.5', profile.description && 'mt-3')}>
              {visibleTags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-card px-2.5 py-0.5 text-xs text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Pitch Deck CTA — takes 1/3. The panel is the frame; the action is a
            CtaButton so it matches every other CTA in the product. */}
        {pitchDeck && onPitchDeckClick && (
          <div className="flex shrink-0 flex-col items-center justify-center gap-3 rounded-xl border border-[#f97316]/20 bg-gradient-to-br from-[#f97316]/[0.08] to-transparent px-8 py-6 lg:w-64">
            <div className="text-center">
              <p className="text-sm font-semibold text-white">Review our pitch deck</p>
              <p className="mt-1 text-xs text-muted-foreground">Vision, traction & fundraising</p>
            </div>
            <CtaButton onClick={onPitchDeckClick} arrow>
              Open pitch deck
            </CtaButton>
          </div>
        )}
      </div>
    </Section>
  )
}
